import {
  SubscriptionPlan,
  SubscriptionFilters,
  SubscriptionStats,
  PlatformPricing,
  FeatureConfiguration,
  LimitsConfiguration,
  DEFAULT_FEATURES,
  DEFAULT_LIMITS,
  SubscriptionStatus,
} from "../types/subscription";
import { ApiService } from "./apiService";
import { logger } from "../utils/logger";

interface CreateSubscriptionPlanRequest {
  name: string;
  description: string;
  platforms: PlatformPricing[] | null;
  features: FeatureConfiguration[];
  isActive?: boolean;
  // isPopular?: boolean;
  trial_days?: number;
  sortOrder?: number;
  sort_order?: number;
  limits: LimitsConfiguration;
  default_speech_to_text_llm_model_id?: string | null;
  default_completion_llm_model_id?: string | null;
}

interface UpdateSubscriptionPlanRequest {
  id: string;
  name?: string;
  description?: string;
  platforms?: PlatformPricing[] | null;
  features?: FeatureConfiguration[];
  status?: SubscriptionStatus;
  isActive?: boolean;
  isPopular?: boolean;
  trial_days?: number;
  sortOrder?: number;
  sort_order?: number;
  limits?: LimitsConfiguration;
  default_speech_to_text_llm_model_id?: string | null;
  default_completion_llm_model_id?: string | null;
}

interface SubscriptionSort {
  field: keyof SubscriptionPlan;
  direction: "asc" | "desc";
}

interface SubscriptionPlansResponse {
  plans: SubscriptionPlan[];
  total: number;
  page: number;
  limit: number;
}

class SubscriptionService {
  private static transformDatabasePlan(dbPlan: any): SubscriptionPlan {
    return {
      id: dbPlan.id,
      name: dbPlan.name,
      description: dbPlan.description || "",
      status: dbPlan.status || (dbPlan.is_active ? "active" : "inactive"),
      isActive: dbPlan.is_active || false,
      is_popular: dbPlan.is_popular || false,
      trial_days: dbPlan.trial_days || 0,
      sortOrder: dbPlan.sort_order || 0,
      platforms: dbPlan.platforms || null,
      features: dbPlan.features?.features || dbPlan.features || [],
      limits: dbPlan.limits || {},
      sort_order: dbPlan.sort_order || 0,
      subscribers_count: dbPlan.subscribers_count || 0,
      total_revenue: dbPlan.total_revenue || 0,
      createdAt: dbPlan.created_at,
      updatedAt: dbPlan.updated_at,
      default_speech_to_text_llm_model_id:
        dbPlan.default_speech_to_text_llm_model_id,
      default_completion_llm_model_id: dbPlan.default_completion_llm_model_id,
    };
  }

  private static generateFeaturesFromDB(
    dbFeatures: any,
    planName: string
  ): FeatureConfiguration[] {
    // If dbFeatures is an array, return it directly
    if (Array.isArray(dbFeatures)) {
      return dbFeatures.map((feature) => ({
        id: feature.id || `feature_${Date.now()}_${Math.random()}`,
        name: feature.name || "",
        description: feature.description || "",
        enabled: feature.enabled !== undefined ? feature.enabled : true,
        value: feature.value,
      }));
    }

    // Otherwise, generate default features based on plan name
    return (
      DEFAULT_FEATURES[planName] || DEFAULT_FEATURES["Personal Plan"] || []
    );
  }

  private static generateLimitsFromDB(dbFeatures: any): LimitsConfiguration {
    // Helper function to handle unlimited vs limited values
    const parseLimit = (value: any, defaultValue: any = "unlimited") => {
      if (value === -1 || value === "unlimited") return "unlimited";
      if (value !== undefined && value !== null) return value;
      return defaultValue;
    };

    return {
      storageGB: parseLimit(dbFeatures.storageGB, 0),
      minutesPerMonth: parseLimit(dbFeatures.minutesPerMonth, "unlimited"),
      summariesPerMonth: parseLimit(dbFeatures.summariesPerMonth, "10"),
      maxDurationMinutes: parseLimit(
        dbFeatures.maxDurationMinutes,
        "unlimited"
      ),
    };
  }

  async getSubscriptionStats(): Promise<SubscriptionStats> {
    try {
      const stats = await ApiService.get<SubscriptionStats>(
        "/subscriptions/statistics"
      );
      return stats;
    } catch (error) {
      logger.error(
        "Failed to get subscription stats:",
        error instanceof Error ? error : undefined
      );
      throw new Error(
        (error as any)?.response?.data?.message ||
          "Failed to get subscription statistics"
      );
    }
  }

  async getSubscriptionPlans(
    filters?: SubscriptionFilters,
    sort?: SubscriptionSort,
    page: number = 1,
    limit: number = 10
  ): Promise<SubscriptionPlansResponse> {
    try {
      const response = await ApiService.get<SubscriptionPlansResponse>(
        "/get-subscription-plans",
        {
          params: {
            ...filters,
            sort: sort?.field,
            sortDirection: sort?.direction.toUpperCase(),
            page,
            limit,
          },
        }
      );

      return response;
    } catch (error) {
      logger.error(
        "Failed to get subscription plans:",
        error instanceof Error ? error : undefined
      );
      throw new Error(
        (error as any)?.response?.data?.message ||
          "Failed to get subscription plans"
      );
    }
  }

  async getSubscriptionPlanNames(): Promise<string[]> {
    try {
      const data = await ApiService.get<{ name: string }[]>(
        "/subscription-plans/names"
      );
      return data?.map((plan) => plan.name) || [];
    } catch (error) {
      logger.error(
        "Failed to get subscription plan names:",
        error instanceof Error ? error : undefined
      );
      return [];
    }
  }

  async getSubscriptionPlan(id: string): Promise<SubscriptionPlan> {
    try {
      const data = await ApiService.get<SubscriptionPlan>(
        `/subscription-plans/${id}`
      );
      return data;
    } catch (error) {
      logger.error(
        "Failed to get subscription plan:",
        error instanceof Error ? error : undefined
      );
      throw new Error(
        (error as any)?.response?.data?.message ||
          "Failed to get subscription plan"
      );
    }
  }

  async createSubscriptionPlan(
    planData: CreateSubscriptionPlanRequest
  ): Promise<SubscriptionPlan> {
    try {
      const data = await ApiService.post<SubscriptionPlan>(
        "/add-subscription-plans",
        planData
      );

      return data;
    } catch (error) {
      logger.error(
        "Failed to create subscription plan:",
        error instanceof Error ? error : undefined
      );
      throw new Error(
        (error as any)?.response?.data?.message ||
          "Failed to create subscription plan"
      );
    }
  }

  async updateSubscriptionPlan(
    planData: UpdateSubscriptionPlanRequest
  ): Promise<SubscriptionPlan> {
    try {
      const updateData: any = {};

      if (planData.name) updateData.name = planData.name;
      if (planData.description) updateData.description = planData.description;
      if (planData.isPopular !== undefined)
        updateData.is_popular = planData.isPopular;
      if (planData.trial_days !== undefined)
        updateData.trial_days = planData.trial_days;
      if (planData.sort_order !== undefined)
        updateData.sort_order = planData.sort_order;
      if (planData.status !== undefined) {
        updateData.status = planData.status;
        updateData.is_active = planData.status === "active";
      }
      if (planData.features) {
        updateData.features = planData.features;
      }
      if (planData.limits) {
        updateData.limits = planData.limits;
      }
      if (planData.platforms) {
        updateData.platforms = planData.platforms;
      }
      if (planData.default_speech_to_text_llm_model_id !== undefined) {
        updateData.default_speech_to_text_llm_model_id =
          planData.default_speech_to_text_llm_model_id;
      }
      if (planData.default_completion_llm_model_id !== undefined) {
        updateData.default_completion_llm_model_id =
          planData.default_completion_llm_model_id;
      }

      const data = await ApiService.put<SubscriptionPlan>(
        `/update-subscription-plans/${planData.id}`,
        updateData
      );

      return data;
    } catch (error) {
      logger.error(
        "Failed to update subscription plan:",
        error instanceof Error ? error : undefined
      );
      throw new Error(
        (error as any)?.response?.data?.message ||
          "Failed to update subscription plan"
      );
    }
  }

  async deleteSubscriptionPlan(id: string): Promise<void> {
    try {
      await ApiService.delete(`/delete-subscription-plans/${id}`);
    } catch (error) {
      logger.error(
        "Failed to delete subscription plan:",
        error instanceof Error ? error : undefined
      );
      throw new Error(
        (error as any)?.response?.data?.message ||
          "Failed to delete subscription plan"
      );
    }
  }

  async togglePlanStatus(
    id: string,
    is_active: boolean
  ): Promise<SubscriptionPlan> {
    try {
      const data = await ApiService.put<SubscriptionPlan>(
        `/toggle-subscription-plans/${id}`,
        { is_active }
      );
      return data;
    } catch (error) {
      logger.error(
        "Failed to toggle plan status:",
        error instanceof Error ? error : undefined
      );
      throw new Error(
        (error as any)?.response?.data?.message ||
          "Failed to toggle plan status"
      );
    }
  }

  generateDefaultPlatformPricing(planName: string): PlatformPricing[] {
    const prices: { [key: string]: { monthly: number; annually: number } } = {
      "Personal Plan": { monthly: 0, annually: 0 },
      "Free Plan": { monthly: 0, annually: 0 },
      "Pro Plan": { monthly: 19.99, annually: 199.99 },
      "Premium Plan": { monthly: 29.99, annually: 299.99 },
      "Enterprise Plan": { monthly: 39.99, annually: 399.99 },
    };

    const planPrices = prices[planName] || prices["Personal Plan"];

    return [
      {
        platform: "web",
        enabled: true,
        pricing: {
          monthly: {
            enabled: true,
            price: planPrices.monthly,
            currency: "USD",
          },
          annually: {
            enabled: true,
            price: planPrices.annually,
            currency: "USD",
          },
        },
      },
      {
        platform: "ios",
        enabled: true,
        pricing: {
          monthly: {
            enabled: true,
            price: planPrices.monthly,
            currency: "USD",
          },
          annually: {
            enabled: true,
            price: planPrices.annually,
            currency: "USD",
          },
        },
      },
      {
        platform: "android",
        enabled: true,
        pricing: {
          monthly: {
            enabled: true,
            price: planPrices.monthly,
            currency: "USD",
          },
          annually: {
            enabled: true,
            price: planPrices.annually,
            currency: "USD",
          },
        },
      },
    ];
  }

  generateDefaultFeatures(planName: string): FeatureConfiguration[] {
    return (
      DEFAULT_FEATURES[planName] || DEFAULT_FEATURES["Personal Plan"] || []
    );
  }

  generateDefaultLimits(planName: string): LimitsConfiguration {
    return (
      DEFAULT_LIMITS[planName] ||
      DEFAULT_LIMITS["Personal Plan"] || {
        storageGB: 20,
        maxDuration: "unlimited",
        minutesPerMonth: 3000,
        summariesPerMonth: "10",
        maxDurationMinutes: 120,
      }
    );
  }

  // New method to get subscription plans with both IDs and names for LLM management
  async getSubscriptionPlansForLLM(): Promise<{ id: string; name: string }[]> {
    try {
      const response = await ApiService.get<any>("get-subscription-plans");
      // Handle both wrapped and unwrapped responses
      if (Array.isArray(response)) {
        return response;
      }
      // If wrapped, extract the plans array
      const plans = response?.data?.plans || response?.data || response || [];
      return Array.isArray(plans) ? plans : [];
    } catch (error) {
      logger.error(
        "Failed to get subscription plans for LLM:",
        error instanceof Error ? error : undefined
      );
      return [];
    }
  }

  // New method to get plan names from IDs
  async getPlanNamesFromIds(
    planIds: string[]
  ): Promise<{ [id: string]: string }> {
    try {
      if (!planIds || planIds.length === 0) {
        return {};
      }

      const data = await ApiService.post<{ id: string; name: string }[]>(
        "/subscription-plans/names",
        { planIds }
      );

      const planMap: { [id: string]: string } = {};
      data?.forEach((plan) => {
        planMap[plan.id] = plan.name;
      });

      return planMap;
    } catch (error) {
      logger.error(
        "Failed to get plan names from IDs:",
        error instanceof Error ? error : undefined
      );
      return {};
    }
  }
}

const subscriptionService = new SubscriptionService();
export { subscriptionService };
export default subscriptionService;
